// VerifyButton — reusable admin QA sign-off control (mig 295).
//
// Renders either a "Verify" button (admin only, when unset) or a
// "Verified · <ago>" badge (when set). One-tap; updates the row's
// verified_at + verified_by columns. Admin-only edit; the badge is
// always visible to everyone so other operators can see the sign-off.

import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ShieldCheck } from 'lucide-react'
import clsx from 'clsx'
import { supabase } from '@/lib/supabase'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { isAdmin } from '@/lib/roles'

import { confirmDialog, alertDialog } from '@/components/Dialogs'
type SupportedTable =
  | 'pallets'
  | 'loading_missions'
  | 'ppecb_consignments'

const PK: Record<SupportedTable, string> = {
  pallets:           'pallet_id',
  loading_missions:  'mission_id',
  ppecb_consignments:'consignment_id',
}

const INVALIDATE: Record<SupportedTable, string[][]> = {
  pallets:           [['pallets'], ['pallet'], ['inspection-queue']],
  loading_missions:  [['loading-missions'], ['missions'], ['dispatch']],
  ppecb_consignments:[['ppecb-consignments-recent'], ['mission-consignment']],
}

function fmtAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const m  = Math.floor(ms / 60_000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

interface Props {
  table:       SupportedTable
  rowId:       string
  verifiedAt:  string | null
  /** Compact mode shrinks the badge for in-list rendering. */
  compact?:    boolean
}

export default function VerifyButton({ table, rowId, verifiedAt, compact = false }: Props) {
  const qc = useQueryClient()
  const { data: me } = useCurrentUser()
  const canVerify   = isAdmin(me?.role)
  const [busy, setBusy] = useState(false)

  const verify = useMutation({
    mutationFn: async () => {
      setBusy(true)
      const { error } = await supabase.from(table)
        .update({
          verified_at: new Date().toISOString(),
          verified_by: me?.id ?? null,
        })
        .eq(PK[table], rowId)
      if (error) throw error
    },
    onSettled: () => {
      setBusy(false)
      for (const key of INVALIDATE[table]) {
        qc.invalidateQueries({ queryKey: key })
      }
    },
    onError: (e: Error) => { void alertDialog({ title: 'Verify failed', message: e.message, tone: 'danger' }) },
  })

  const unverify = useMutation({
    mutationFn: async () => {
      if (!(await confirmDialog({ message: 'Remove the admin-verified sign-off on this row?', tone: 'danger' }))) {
        throw new Error('cancelled')
      }
      setBusy(true)
      const { error } = await supabase.from(table)
        .update({ verified_at: null, verified_by: null })
        .eq(PK[table], rowId)
      if (error) throw error
    },
    onSettled: () => {
      setBusy(false)
      for (const key of INVALIDATE[table]) {
        qc.invalidateQueries({ queryKey: key })
      }
    },
    onError: (e: Error) => { if (e.message !== 'cancelled') void alertDialog({ title: 'Unverify failed', message: e.message, tone: 'danger' }) },
  })

  const sizeClass = compact ? 'text-[10px] px-2 py-0.5' : 'text-xs px-2.5 py-1'

  const ago = useMemo(() => verifiedAt ? fmtAgo(verifiedAt) : null, [verifiedAt])

  if (verifiedAt) {
    return (
      <button
        onClick={() => canVerify && unverify.mutate()}
        disabled={!canVerify || busy}
        title={canVerify ? 'Tap to remove sign-off' : 'Admin-verified'}
        className={clsx(
          'inline-flex items-center gap-1 rounded-full font-semibold',
          'bg-emerald-900/40 text-emerald-300 border border-emerald-700/60',
          canVerify && 'hover:bg-emerald-900/60 cursor-pointer',
          !canVerify && 'cursor-default',
          sizeClass,
        )}
      >
        <ShieldCheck size={compact ? 10 : 12} />
        <span>Verified · {ago}</span>
      </button>
    )
  }

  if (!canVerify) return null

  return (
    <button
      onClick={() => verify.mutate()}
      disabled={busy}
      title="Admin sign-off — mark this row as reviewed"
      className={clsx(
        'inline-flex items-center gap-1 rounded-full font-semibold',
        'surface-raised text-fg-secondary hover:bg-emerald-700 hover:text-white',
        'border border-line-strong hover:border-emerald-600',
        sizeClass,
      )}
    >
      <ShieldCheck size={compact ? 10 : 12} />
      <span>{busy ? 'Verifying…' : 'Verify'}</span>
    </button>
  )
}
